import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import {
  Figure,
  LoadMore,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  caseHref,
} from '@/pages/cvm/shared';
import { cvmPipelineParams, fetchCvmPipeline } from '@/services/cvm';
import type { CvmFunnelStep, CvmRegisterRow } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * The open-data register of sanctioning processes: its own population.
 *
 * Every PAS that reached the defence phase since the register began, with its
 * current phase and one status event per accused. It is NOT the judgment
 * corpus and is never divided by it: the shape panel says how many register
 * processes have a judgment here, the funnel counts status events by their
 * wording, and the collection card refuses payment analysis because a "GRU
 * sent" event carries no amount and no due date. Accused are counted per
 * process here and named only on the case page.
 */
export function CvmPipelinePage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const [phase, setPhase] = useState(seed.get('phase') ?? '');
  const filters = { q, phase: phase || null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'pipeline', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmPipeline({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const phases = data?.phase_mix.value?.map((s) => s.code) ?? [];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:pipeline.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:pipeline.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:pipeline.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <>
          <section className="rounded-lg border bg-card p-4" data-shape>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:pipeline.shape.title')}
            </h2>
            <Answer answer={data.shape} help={t('cvm:pipeline.shape.help')}>
              {(s) => (
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  <Figure
                    label={t('cvm:pipeline.shape.processes')}
                    value={number.format(s.processes)}
                  />
                  <Figure
                    label={t('cvm:pipeline.shape.events')}
                    value={number.format(s.accused_events)}
                  />
                  <Figure
                    label={t('cvm:pipeline.shape.names')}
                    value={number.format(s.accused_names)}
                  />
                  <Figure
                    label={t('cvm:pipeline.shape.withJudgment')}
                    value={number.format(s.with_judgment)}
                    href="/cvm/cases"
                  />
                  <Figure
                    label={t('cvm:pipeline.shape.matched')}
                    value={number.format(s.events_matched_to_party)}
                  />
                  <Figure
                    label={t('cvm:pipeline.shape.window')}
                    value={s.first_opened ?? '—'}
                    hint={
                      s.last_moved
                        ? t('cvm:pipeline.shape.lastMoved', {
                            date: s.last_moved,
                          })
                        : undefined
                    }
                  />
                </dl>
              )}
            </Answer>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <OutcomeMixPanel
              answer={data.phase_mix}
              titleKey="cvm:pipeline.phaseMix.title"
              helpKey="cvm:pipeline.phaseMix.help"
              labelPrefix="cvm:phases."
              rowHref={(code) => `/cvm/pipeline?phase=${encodeURIComponent(code)}`}
            />
            <section className="rounded-lg border bg-card p-4" data-funnel>
              <h2 className="mb-2 text-sm font-semibold text-foreground">
                {t('cvm:pipeline.funnel.title')}
              </h2>
              <Answer answer={data.funnel} help={t('cvm:pipeline.funnel.help')}>
                {(steps: CvmFunnelStep[]) => (
                  <div className="max-h-80 overflow-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead className={THEAD}>
                        <tr>
                          <th className={TH}>{t('cvm:pipeline.funnel.situacao')}</th>
                          <th className={TH_NUM}>{t('cvm:pipeline.funnel.events')}</th>
                          <th className={TH_NUM}>{t('cvm:pipeline.funnel.processes')}</th>
                          <th className={TH_NUM}>{t('cvm:pipeline.funnel.names')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {steps.map((step) => (
                          <tr key={step.situacao} className="border-t">
                            <td className={TD}>{step.situacao}</td>
                            <td className={TD_NUM}>{number.format(step.events)}</td>
                            <td className={TD_NUM}>{number.format(step.processes)}</td>
                            <td className={TD_NUM}>{number.format(step.names)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Answer>
            </section>
          </div>

          <RefusalCard title={t('cvm:pipeline.collection.title')} answer={data.collection} />
        </>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="cvm-pipeline-query" className="text-xs font-medium text-foreground">
            {t('cvm:pipeline.search.label')}
          </label>
          <input
            id="cvm-pipeline-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cvm:pipeline.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="cvm-pipeline-phase" className="block text-xs font-medium text-foreground">
            {t('cvm:pipeline.filters.phase')}
          </label>
          <select
            id="cvm-pipeline-phase"
            value={phase}
            onChange={(event) => setPhase(event.target.value)}
            className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('cvm:pipeline.filters.any')}</option>
            {phases.map((code) => (
              <option key={code} value={code}>
                {t(`cvm:phases.${code}`, { defaultValue: code })}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('cvm:pipeline.search.submit')}
        </button>
        <div className="ml-auto">
          <ExportButton
            path={`/api/v1/cvm/export/pipeline?${cvmPipelineParams(filters).toString()}`}
          />
        </div>
      </form>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:pipeline.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:pipeline.columns.process')}</th>
                  <th className={TH}>{t('cvm:pipeline.columns.opened')}</th>
                  <th className={TH}>{t('cvm:pipeline.columns.unit')}</th>
                  <th className={TH}>{t('cvm:pipeline.columns.phase')}</th>
                  <th className={TH}>{t('cvm:pipeline.columns.lastMoved')}</th>
                  <th className={TH_NUM}>{t('cvm:pipeline.columns.accused')}</th>
                  <th className={TH}>{t('cvm:pipeline.columns.ementa')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <RegisterRow key={row.nup_digits} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      <FieldGuide i18nKey="cvm:pipeline.guide" />
    </div>
  );
}

function RegisterRow({ row }: { row: CvmRegisterRow }) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD}>
        {row.case_key ? (
          <Link
            to={caseHref(row.case_key)}
            className="text-primary underline-offset-2 hover:underline"
          >
            {row.nup_display}
          </Link>
        ) : (
          row.nup_display
        )}
        {row.case_key ? (
          <div className="text-xs text-muted-foreground">{t('cvm:pipeline.judged')}</div>
        ) : null}
      </td>
      <td className={TD_MUTED}>{row.opened_at ?? '—'}</td>
      <td className={TD_MUTED}>{row.instructing_unit ?? '—'}</td>
      <td className={TD_MUTED}>
        <span className="text-foreground">{row.phase ?? '—'}</span>
        {row.subphase ? <div>{row.subphase}</div> : null}
        {row.location ? <div>{row.location}</div> : null}
      </td>
      <td className={TD_MUTED}>{row.last_moved_at ?? '—'}</td>
      <td className={TD_NUM}>{number.format(row.accused)}</td>
      <td className={TD_MUTED}>{row.ementa ?? '—'}</td>
    </tr>
  );
}
